import HistoryIcon from "@mui/icons-material/History";
import {
  Avatar,
  Box,
  Card,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ReactAudioPlayer from "react-audio-player";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { ReactComponent as ArrowBack } from "../../../assets/icons/arrow-back.svg";
import { setEncryptedItem } from "../../../utils/localStorageHelper";
import CallTranscriptModal from "../CallHistory/CallTranscriptModal";
import Insight from "../Campaigns/CampaignDetails/Insight";
import BaseButton from "../Common/Buttons/BaseButton";
import EmptyState from "../Common/EmptyState";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import PageLoader from "../CommonComponents/PageLoader";
import {
  getLeadsDataAction,
  getProjectsAction,
} from "../Projects/Store/projects.action";
import LeadHistory from "./LeadHistory";
import { getWorkflowsAction } from "./Store/workflows.action";

const LeadDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { leadId, workflowId } = useParams();
  const theme = useTheme();
  const brandColor = theme.custom.brandColor;
  const reducer = useSelector((reducer: any) => reducer);
  const {
    ProjectReducer: { leadsList },
  } = reducer;

  const [selectedLead, setSelectedLead] = useState(null) as any;
  const [actionHistory, setActionHistory] = useState([]) as any;
  const [workflows, setWorkflows] = useState([]) as any;
  const [selectedWorkflow, setSelectedWorkflow] = useState(null) as any;
  const [historyWorkflows, setHistoryWorkflows] = useState<any[]>([]);
  const [callDetails, setCallDetails] = useState(null) as any;
  const [summaryModal, setSummaryModal] = useState(false);
  const [insightsModal, setInsightsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audioKey, setAudioKey] = useState(0);

  useEffect(() => {
    fetchLeadsData();
  }, [leadId]);

  useEffect(() => {
    setAudioKey((prev) => prev + 1);
  }, [selectedWorkflow?.id, callDetails?.call_uuid]);

  useEffect(() => {
    const selected = leadsList?.find((lead) => lead?._id === leadId);
    setSelectedLead(selected);

    if (selected?.history?.length > 0 && workflows?.length > 0) {
      const workflowIds = [
        ...new Set(selected.history.map((h) => h?.workflowId)),
      ];

      const filteredWorkflows = workflowIds
        .map((id) => workflows.find((wf: any) => wf?.id === id))
        .filter(Boolean);

      setHistoryWorkflows(filteredWorkflows);

      const currentWorkflow =
        filteredWorkflows.find((wf) => wf.id === workflowId) ||
        filteredWorkflows[0];

      setSelectedWorkflow(currentWorkflow);

      if (currentWorkflow) {
        const filtered = selected.history.filter(
          (action) => action?.workflowId === currentWorkflow.id
        );
        setActionHistory(filtered);

        if (filtered.some(({ action }) => action?.action === "call")) {
          const details = selected.history?.find((item) => item?.call_uuid);
          setCallDetails(details);
        } else {
          setCallDetails(null);
        }
      }
    }
  }, [leadsList, workflows, leadId, workflowId]);

  useEffect(() => {
    const init = async () => {
      const res: any = await dispatch(getWorkflowsAction());
      const workflowData = res
        ?.map((item) => {
          if (item?.type === "workflow") {
            return item;
          }
        })
        ?.filter(Boolean);

      const workflowLists = workflowData?.map((wf) => ({
        id: wf._id,
        name: wf?.workflows?.name,
      }));

      if (workflowLists?.length > 0) {
        setSelectedWorkflow(
          workflowLists?.find((workflow) => workflow?.id === workflowId)
        );
        setWorkflows(workflowLists);
      }
    };

    init();
  }, []);

  const fetchLeadsData = async () => {
    setLoading(true);
    const params = {
      pageIndex: 1,
      pageSize: 1000,
      searchtext: "",
    };
    await dispatch(getLeadsDataAction(params));
    setLoading(false);
    await dispatch(getProjectsAction());
  };

  const handleWorkflowChange = (e) => {
    const newWorkflowId = e.target.value;
    history.push(`/account/lead-details/${leadId}/${newWorkflowId}`);
  };

  if (loading) {
    return <PageLoader height="80vh" />;
  }

  return (
    <Box py={1}>
      <Box className="flex-between mb-4">
        <Typography className="font-20-grey capitalize ">
          Workflow History {selectedWorkflow && "- "}
          {selectedWorkflow?.name}
        </Typography>

        <Box className="flex-only gap-2">
          {historyWorkflows.length > 0 && (
            <Select
              size="small"
              value={selectedWorkflow?.id || ""}
              onChange={handleWorkflowChange}
              sx={{
                minWidth: 200,
                fontSize: "14px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              }}
            >
              {historyWorkflows.map((wf) => (
                <MenuItem key={wf.id} value={wf.id} sx={{ fontSize: "14px" }}>
                  {wf.name}
                </MenuItem>
              ))}
            </Select>
          )}
          <BaseButton
            title="Back"
            handleClick={() => history.push("/account/leads")}
            variant="contained"
            startIcon={<ArrowBack className="back-icon" />}
          />
        </Box>
      </Box>
      {selectedLead ? (
        <>
          <Card
            sx={{
              border: "1px solid rgba(229, 231, 235, 1)",
              minHeight: "110px",
              width: "300px",
              borderRadius: "12px",
              padding: "18px 22px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
            elevation={0}
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Box display="flex" alignItems="center" gap={1.8}>
                <Avatar
                  sx={{
                    bgcolor: brandColor,
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    width: 42,
                    height: 42,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  {`${selectedLead?.firstName?.[0] || ""}${
                    selectedLead?.lastName?.[0] || ""
                  }`}
                </Avatar>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: "#374151" }}
                >
                  {selectedLead?.firstName} {selectedLead?.lastName}
                </Typography>
              </Box>

              <Box pl={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    {selectedLead?.phone || "No phone number"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>
          {actionHistory?.length > 0 && (
            <>
              <Box className="flex-between my-1">
                <>
                  <Typography
                    className="font-16-700-grey my-4 flex-align-center"
                    gap={0.5}
                  >
                    <HistoryIcon />
                    Action History
                  </Typography>
                </>
                <Box className="flex-xy-center gap-2">
                  {callDetails && callDetails?.callRecordingUrl && (
                    <Box className="mt-1">
                      <ReactAudioPlayer
                        key={audioKey}
                        src={
                          callDetails?.callRecordingUrl ||
                          `https://storage.googleapis.com/botstream-recordings/${callDetails?.call_uuid}.mp3`
                        }
                        controls
                        style={{
                          width: "300px",
                          height: "40px",
                          minWidth: "300px",
                        }}
                      />
                    </Box>
                  )}
                  {callDetails && callDetails?.call_uuid && (
                    <BaseButton
                      variant="contained"
                      title="View Transcript"
                      handleClick={() => setSummaryModal(true)}
                    />
                  )}
                  {callDetails && callDetails?.campaignRunId && (
                    <BaseButton
                      variant="contained"
                      title="View Insight"
                      handleClick={() => setInsightsModal(true)}
                    />
                  )}
                </Box>
              </Box>
              <LeadHistory
                workflows={workflows}
                selectedLead={selectedLead}
                actionHistory={actionHistory}
              />
            </>
          )}
        </>
      ) : (
        <Box className="flex-xy-center">
          <EmptyState message="No Action History Found" />
        </Box>
      )}
      {summaryModal && (
        <CallTranscriptModal
          show={summaryModal}
          onClose={() => setSummaryModal((prev) => !prev)}
          selectedTranscript={callDetails}
        />
      )}
      {insightsModal && (
        <CommonModal
          show={insightsModal}
          toggle={() => setInsightsModal(false)}
          size={1100}
          handleSave={() => setInsightsModal(false)}
          heading="View Insight"
          body={
            <Insight
              campaignName=""
              campaignRunId={callDetails?.campaignRunId}
              projectID={callDetails?.projectId}
            />
          }
          saveButtonText="Close"
        />
      )}
    </Box>
  );
};

export default LeadDetails;
